// TurnierInfoPage.jsx — öffentliche Ausschreibung eines Turniers (/event/:id)
//
// Seit 22.10.2026 nach dem abgenommenen Muster (docs/muster-ausschreibung):
// kompakter Kopf mit Anmeldekasten, Sprungleiste, alle Abschnitte auf einer
// Seite. Jeder Abschnitt erscheint nur, wenn er Inhalt hat; ein Turnier ohne
// die neuen Angaben sieht deshalb nicht leer aus.
//
// Behoben dabei:
//   - „Anmeldung geschlossen“ neben „Jetzt anmelden“ ohne Anmeldeschluss:
//     offen/zu sagt jetzt nur der Server (utils/anmeldeZustand.js).
//   - „Veranstalter“ stand im Code, kam aber nie von der API.
//   - Feste Texte (Barzahlung vor Ort, Stornierung, info@tda-intl.com) sind
//     weg; es steht nur, was das Turnier selbst angibt.
// Bankdaten liefert die API nicht; die Zahlungsangabe schreibt der
// Veranstalter selbst unter „Zahlungsweise“.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import {
  Icon, StatusAbzeichen, knopfFuer, tag, d2, datKurz, datLang, euro, logoFuer,
  useThema, ThemaKnopf, OeffentlicherFuss,
} from '../components/oeffentlich/ausschreibungTeile';
import { anzeigeName, altersText } from '../lib/kategorieText';
import { geschlechtSchluessel, hatGewichtsklasse } from '../lib/divisionsFilter';
import '../styles/ausschreibung.css';

const KURZ = {
  ueberblick: 'Überblick', zeitplan: 'Zeitplan', kategorien: 'Kategorien', startgeld: 'Startgeld', anfahrt: 'Anfahrt',
  regeln: 'Regeln', teilnahme: 'Teilnahme', vorort: 'Vor Ort', kontakt: 'Kontakt', downloads: 'Downloads',
};
const G_TEXT = { m: 'männlich', f: 'weiblich', mixed: 'gemischt', x: 'offen' };
const ALTERS_REGEL = { turniertag: 'am Turniertag', jahresende: 'am 31.12. des Turnierjahres', custom: 'am Stichtag' };

/** Schlüssel-Wert-Liste; Paare ohne Wert fallen weg. */
function KV({ paare }) {
  const p = paare.filter(([, w]) => w !== null && w !== undefined && w !== '' && w !== false);
  if (!p.length) return null;
  return (
    <dl className="au-kv">
      {p.map(([k, w]) => <React.Fragment key={k}><dt>{k}</dt><dd className="au-text-block">{w}</dd></React.Fragment>)}
    </dl>
  );
}

function Abschnitt({ id, titel, icon, children }) {
  return <section className="au-abschnitt" id={id}><h2><Icon n={icon} />{titel}</h2>{children}</section>;
}

const betrag = (x) => (x === null || x === undefined || x === '' ? null : Number(x));
const groesse = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const uhr = (z) => (z ? `${z} Uhr` : '');

/** Zeitpunkt, bis zu dem gezählt wird: Ende des Schlusstags oder Turnierbeginn. */
function zielZeit(a, einlass) {
  const d = tag(a?.bis);
  if (!d) return null;
  if (a.bis_turniertag) {
    const [h, m] = String(einlass || '00:00').split(':').map(Number);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h || 0, m || 0);
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
}

function Zaehler({ ziel }) {
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => { const i = setInterval(() => setJetzt(Date.now()), 30000); return () => clearInterval(i); }, []);
  const ms = Math.max(0, ziel - jetzt);
  const tg = Math.floor(ms / 86400000);
  const st = Math.floor((ms % 86400000) / 3600000);
  const mi = Math.floor((ms % 3600000) / 60000);
  return (
    <div className="au-zaehler" aria-label={`${tg} Tage, ${st} Stunden, ${mi} Minuten`}>
      <div><b>{tg}</b><small>Tage</small></div><div><b>{st}</b><small>Std</small></div><div><b>{mi}</b><small>Min</small></div>
    </div>
  );
}

export default function TurnierInfoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { thema, wirksam, umschalten } = useThema();
  const [t, setT] = useState(null);
  const [fehler, setFehler] = useState('');
  const [tagWahl, setTagWahl] = useState(null);
  const [kf, setKf] = useState({ typ: '', g: '', jahr: '', q: '' });
  const [aktiv, setAktiv] = useState('');
  const [klein, setKlein] = useState(false);
  const [gross, setGross] = useState(false);
  const kopfRef = useRef(null);
  const leisteRef = useRef(null);
  const mitLeiste = !!t && (t.anmeldung?.anmeldbar || t.anmeldung?.zustand === 'voll');

  // Chat-Blase über die feste Anmeldeleiste heben (22.10.2026): Besucher-Chat
  // (#vc-btn aus dem Widget in public/index.html) und KI-Knopf lagen am Handy
  // auf dem Anmeldeknopf. Gemessen wird die echte Höhe der Leiste (sie ist
  // nur am schmalen Schirm sichtbar), CSS in styles/ausschreibung.css liest
  // --anmeldeleiste-hoehe. Ausgeblendet wird nichts.
  useEffect(() => {
    const el = leisteRef.current;
    const wurzel = document.documentElement;
    if (!el) return undefined;
    const messen = () => {
      const h = getComputedStyle(el).display === 'none' ? 0 : Math.ceil(el.getBoundingClientRect().height);
      if (h > 0) {
        wurzel.style.setProperty('--anmeldeleiste-hoehe', `${h}px`);
        wurzel.classList.add('mit-anmeldeleiste');
      } else {
        wurzel.classList.remove('mit-anmeldeleiste');
        wurzel.style.removeProperty('--anmeldeleiste-hoehe');
      }
    };
    messen();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(messen) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', messen);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', messen);
      wurzel.classList.remove('mit-anmeldeleiste');
      wurzel.style.removeProperty('--anmeldeleiste-hoehe');
    };
  }, [mitLeiste]);

  useEffect(() => {
    let weg = false;
    setT(null); setFehler('');
    fetch(`/api/turniere/public/${id}/info`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (weg) return;
        if (!r.ok || !d.success) setFehler(r.status === 404 ? 'Dieses Turnier ist nicht (mehr) veröffentlicht.' : (d.error || `Fehler ${r.status}`));
        else setT(d.data);
      })
      .catch(() => { if (!weg) setFehler('Verbindungsfehler. Bitte später neu laden.'); });
    return () => { weg = true; };
  }, [id]);

  const anmelden = () => {
    // Wie bisher: Athletenkonto → eigenes Dashboard, Verein → Anmeldung, sonst Anmelden-Seite
    let athlet = null; let verein = null;
    try { athlet = localStorage.getItem('athleteToken'); verein = localStorage.getItem('token'); } catch { /* ohne Speicher: wie abgemeldet */ }
    if (athlet) navigate('/athlete/dashboard');
    else if (verein) navigate(`/registration?turnierId=${id}`);
    else navigate(`/anmelden?turnier=${id}`);
  };

  // Programm nach Tagen
  const tage = useMemo(() => {
    const m = new Map();
    for (const p of t?.programm || []) {
      const k = p.tag || '';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    }
    return [...m.entries()].map(([k, punkte]) => ({ k, d: tag(k), punkte }));
  }, [t]);

  const divisionen = useMemo(() => t?.divisionen || [], [t]);
  const typen = useMemo(() => [...new Set(divisionen.map((d) => anzeigeName(d.typ || 'Sonstige')))], [divisionen]);
  const stichtag = tag(t?.alters_stichtag);
  const passend = useMemo(() => {
    const q = kf.q.trim().toLowerCase();
    const jg = /^\d{4}$/.test(kf.jahr) ? Number(kf.jahr) : null;
    // Alter am Stichtag aus dem Jahrgang: am 31.12. eindeutig, sonst eins von zwei.
    const alter = jg && stichtag ? (stichtag.getMonth() === 11 && stichtag.getDate() === 31
      ? [stichtag.getFullYear() - jg] : [stichtag.getFullYear() - jg - 1, stichtag.getFullYear() - jg]) : null;
    return divisionen.filter((d) => {
      if (kf.typ && anzeigeName(d.typ || 'Sonstige') !== kf.typ) return false;
      const g = geschlechtSchluessel(d.geschlecht);
      if (kf.g && g !== kf.g && g !== 'mixed' && g !== 'x') return false;
      if (alter && !alter.some((a) => (d.alter_von == null || a >= d.alter_von) && (d.alter_bis == null || a <= d.alter_bis))) return false;
      if (q && !`${d.code} ${d.name} ${d.typ} ${d.stufe || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [divisionen, kf, stichtag]);

  // Sprungleiste: aktiven Abschnitt markieren, kleiner Anmeldeknopf ohne Kopf
  useEffect(() => {
    if (!t) return undefined;
    const lauf = () => {
      const links = [...document.querySelectorAll('.au-sprung-in a[href^="#"]:not(.au-mini)')];
      let a = links[0]?.getAttribute('href') || '';
      for (const l of links) {
        const z = document.querySelector(l.getAttribute('href'));
        if (z && z.getBoundingClientRect().top < 220) a = l.getAttribute('href');
      }
      setAktiv(a);
      if (kopfRef.current) setKlein(kopfRef.current.getBoundingClientRect().bottom < 100);
    };
    lauf();
    window.addEventListener('scroll', lauf, { passive: true });
    return () => window.removeEventListener('scroll', lauf);
  }, [t]);

  const rahmen = (inhalt) => (
    <div className="au" data-thema={thema || undefined}>
      <PublicNav />
      {inhalt}
      <OeffentlicherFuss />
    </div>
  );
  if (fehler) return rahmen(<div className="au-wrap"><div className="au-fehler" role="alert">{fehler}</div><p style={{ textAlign: 'center' }}><Link to="/">Alle Veranstaltungen</Link></p></div>);
  if (!t) return rahmen(<div className="au-laden">Ausschreibung wird geladen …</div>);

  const a = t.anmeldung || {};
  const knopf = knopfFuer(a);
  const gb = t.gebuehren || {};
  const von = tag(t.datum_iso);
  const bis = t.end_datum_iso && t.end_datum_iso !== t.datum_iso ? tag(t.end_datum_iso) : null;
  const halle = t.halle_name || t.ort || '';
  const anschrift = [t.adresse, [t.plz, t.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const anschriftZeigen = anschrift && anschrift !== halle ? anschrift : '';
  const kategorien = t.kategorien || [];
  const preisAb = betrag(gb.einzel_erste);
  // Ohne Meldeschluss läuft kein Countdown mehr: gemeldet werden kann auch am
  // Turniertag, bis die jeweilige Kategorie startet (utils/kategorieStart.js).
  const ziel = a.anmeldbar && !a.bis_kategoriestart ? zielZeit(a, t.einlass_zeit) : null;
  const anteil = t.max_teilnehmer ? Math.min(100, Math.round((t.gemeldet / t.max_teilnehmer) * 100)) : 0;
  const beschraenkt = t.anmeldung_beschraenkung === 'nur_spezifische';
  const waage = t.einwaage_von_uhr ? `${t.einwaage_tag && t.einwaage_tag !== t.datum_iso ? `${datKurz(tag(t.einwaage_tag))}, ` : ''}${t.einwaage_von_uhr}${t.einwaage_bis_uhr ? ` bis ${t.einwaage_bis_uhr}` : ''} Uhr` : '';
  const ausrichter = t.ausrichter_name || '';
  const veranstalter = t.veranstalter_name || t.verband_name || '';

  const knopfKlick = () => {
    if (knopf.ziel === 'anmelden') anmelden();
    else if (knopf.ziel === 'live' && t.live_url) navigate(t.live_url);
  };
  const knopfAus = !knopf.ziel || (knopf.ziel === 'live' && !t.live_url);

  // ── Abschnitte (nur mit Inhalt) ──
  const teile = [];
  const dazu = (id2, titel, icon, inhalt, leer) => { if (!leer) teile.push({ id: id2, titel, icon, inhalt }); };

  dazu('ueberblick', 'Überblick', 'info', <>
    {t.beschreibung && <p className="au-text-block">{t.beschreibung}</p>}
    <KV paare={[
      ['Veranstalter', veranstalter], ['Ausrichter', ausrichter !== veranstalter ? ausrichter : ''], ['Wettkampfleitung', t.wettkampfleitung],
      ['Disziplinen', kategorien.length ? kategorien.map((k) => <span key={k} className="au-diszi">{k}</span>) : null],
      ['Hinweise', t.hinweise],
    ]} />
    {beschraenkt && <div className="au-hinweis"><Icon n="info" /><span>Anmeldung nur für zugelassene Vereine.</span></div>}
  </>, !(t.beschreibung || veranstalter || ausrichter || t.wettkampfleitung || kategorien.length || t.hinweise));

  if (tage.length) {
    const aktuell = tage.find((x) => x.k === tagWahl) || tage.find((x) => x.k === t.datum_iso) || tage[0];
    dazu('zeitplan', 'Zeitplan', 'uhr', <>
      {tage.length > 1 && (
        <div className="au-tage" role="group" aria-label="Tag">
          {tage.map((x) => (
            <button type="button" key={x.k} aria-pressed={x === aktuell} onClick={() => setTagWahl(x.k)}>
              {x.d ? `${['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][x.d.getDay()]} ${d2(x.d.getDate())}.${d2(x.d.getMonth() + 1)}.` : 'Ohne Tag'}
            </button>
          ))}
        </div>
      )}
      <ol className="au-zs">
        {aktuell.punkte.map((p) => (
          <li key={p.id} className={p.art === 'wettkampf' ? 'au-w' : p.art === 'siegerehrung' ? 'au-e' : ''}>
            <div className="au-zeit">{p.von || ''}{p.bis && <small>bis {p.bis}</small>}</div>
            <div className="au-pkt" />
            <div className="au-was"><strong>{p.titel}</strong>{p.flaeche && <span className="au-matte">{p.flaeche}</span>}{p.text && <div>{p.text}</div>}</div>
          </li>
        ))}
      </ol>
      <div className="au-legende">
        <span><i />Ablauf</span><span><i style={{ borderColor: 'var(--au-rot)' }} />Wettkampf</span>
        <span><i style={{ borderColor: 'var(--au-gold)', background: 'var(--au-gold)' }} />Siegerehrung</span>
      </div>
    </>);
  }

  dazu('kategorien', 'Kategorien', 'gruppe', divisionen.length ? <>
    {typen.length > 1 && (
      <div className="au-kf au-chips" role="group" aria-label="Disziplin">
        <button type="button" className="au-chip" aria-pressed={!kf.typ} onClick={() => setKf((x) => ({ ...x, typ: '' }))}>Alle</button>
        {typen.map((ty) => <button type="button" key={ty} className="au-chip" aria-pressed={kf.typ === ty} onClick={() => setKf((x) => ({ ...x, typ: ty }))}>{ty}</button>)}
      </div>
    )}
    <div className="au-kf">
      <select aria-label="Geschlecht" value={kf.g} onChange={(e) => setKf((x) => ({ ...x, g: e.target.value }))}>
        <option value="">Alle Geschlechter</option><option value="m">männlich</option><option value="f">weiblich</option>
      </select>
      <label style={{ fontSize: 13, color: 'var(--au-text-2)' }}>Jahrgang{' '}
        <input type="number" min="1930" max="2030" placeholder="z. B. 2013" value={kf.jahr} onChange={(e) => setKf((x) => ({ ...x, jahr: e.target.value }))} />
      </label>
      <input type="search" placeholder="Code oder Name" aria-label="Kategorie suchen" value={kf.q} onChange={(e) => setKf((x) => ({ ...x, q: e.target.value }))} style={{ flex: 1, minWidth: 140 }} />
      <span style={{ fontSize: 13, color: 'var(--au-text-3)' }}>{passend.length} von {divisionen.length}</span>
    </div>
    <div className="au-tab-wrap">
      <table className="au-kt">
        <thead><tr><th>Kategorie</th><th>Alter</th><th>Geschlecht</th><th>Stufe</th><th>Gewicht</th><th>Code</th><th style={{ textAlign: 'right' }}>Gemeldet</th></tr></thead>
        <tbody>
          {passend.length ? passend.slice(0, 400).map((d) => {
            const g = G_TEXT[geschlechtSchluessel(d.geschlecht)] || d.geschlecht || '';
            const gew = hatGewichtsklasse({ Minimun_Weight: d.gewicht_von, Maximum_Weight: d.gewicht_bis }) ? `${d.gewicht_von}–${d.gewicht_bis} kg` : '';
            const alter = altersText(d.alter_text) || (d.alter_von != null ? `${d.alter_von}–${d.alter_bis}` : '');
            return (
              <tr key={d.code} className={d.gestartet ? 'au-gestartet' : undefined} title={d.gestartet ? `Bereits gestartet: ${d.gestartet_grund || ''}` : undefined}>
                <td className="au-diszi-zelle au-name-zelle">{anzeigeName(d.typ)}<small className="au-h">{anzeigeName(d.name)}</small>
                  {d.gestartet && <small className="au-start-hinweis">bereits gestartet{d.gestartet_grund ? ` · ${d.gestartet_grund}` : ''}</small>}</td>
                <td className="au-h">{alter || '–'}</td><td className="au-h">{g || '–'}</td>
                <td className="au-h">{d.stufe || '–'}</td><td className="au-h">{gew || '–'}</td>
                <td className="au-code">{d.code}</td>
                <td className={`au-zahl${d.gemeldet < 3 ? ' au-wenig' : ''}`}>{d.gemeldet}</td>
                <td className="au-rest">{[alter, g, d.stufe, gew].filter(Boolean).join(' · ')}</td>
              </tr>
            );
          }) : <tr><td colSpan={7} style={{ padding: 18, color: 'var(--au-text-3)' }}>Keine Kategorie passt.</td></tr>}
        </tbody>
      </table>
    </div>
    <div className="au-k-info">
      {passend.length > 400 ? `Gezeigt werden die ersten 400 von ${passend.length}, bitte weiter eingrenzen. ` : ''}
      {stichtag ? `Maßgeblich ist das Alter ${ALTERS_REGEL[t.altersberechnung_stichtag] || 'am Stichtag'} (${datKurz(stichtag)}). ` : ''}
      Gelb: noch wenige Meldungen.
      {divisionen.some((d) => d.gestartet) ? ' Ausgegraut: bereits gestartet, dort ist keine Anmeldung mehr möglich.' : ''}
    </div>
    {(t.altersklassen || t.gewichtsklassen) && <KV paare={[['Altersklassen', t.altersklassen], ['Gewichtsklassen', t.gewichtsklassen]]} />}
  </> : <>
    {kategorien.length > 0 && <p>{kategorien.map((k) => <span key={k} className="au-diszi">{k}</span>)}</p>}
    <KV paare={[['Altersklassen', t.altersklassen], ['Gewichtsklassen', t.gewichtsklassen]]} />
  </>, !divisionen.length && !t.altersklassen && !t.gewichtsklassen);

  const zeitraeume = gb.zeitraeume || [];
  const preisZeilen = [
    ['Erste Kategorie', null, betrag(gb.einzel_erste)],
    betrag(gb.einzel_zweite) !== betrag(gb.einzel_erste) ? ['Zweite Kategorie', null, betrag(gb.einzel_zweite)] : null,
    betrag(gb.einzel_weitere) !== betrag(gb.einzel_zweite) ? ['Ab der dritten Kategorie', null, betrag(gb.einzel_weitere)] : null,
    gb.team != null ? ['Team', t.teamgebuehr_modus === 'verein' ? 'je Team, auf der Vereinsrechnung' : 'je Team, aufgeteilt auf die Mitglieder', betrag(gb.team)] : null,
    ...zeitraeume.map((z) => [`Frühbucher${z.bezeichnung ? ` „${z.bezeichnung}“` : ''}`, `${datKurz(tag(z.von))} bis ${datKurz(tag(z.bis))}, auf jeden Start`, `−${Number(z.rabatt_prozent).toLocaleString('de-DE')} %`]),
    betrag(gb.nachmeldegebuehr) > 0 ? ['Nachmeldung', 'Aufschlag je Start', `+ ${euro(gb.nachmeldegebuehr)}`] : null,
    betrag(t.ummeldegebuehr) > 0 ? ['Ummeldung', 'Gewichts- oder Altersklasse', `+ ${euro(t.ummeldegebuehr)}`] : null,
    betrag(t.protestgebuehr) > 0 ? ['Protestgebühr', null, euro(t.protestgebuehr)] : null,
  ].filter((z) => z && z[2] !== null && z[2] !== undefined && !Number.isNaN(z[2]));
  const fruehAktiv = gb.fruehbucher && gb.fruehbucher.prozent > 0;
  dazu('startgeld', 'Startgeld und Zahlung', 'euro', <>
    {preisZeilen.length > 0 && (
      <table className="au-pt"><tbody>
        {preisZeilen.map(([k, s, w]) => <tr key={k}><td>{k}{s && <small>{s}</small>}</td><td>{typeof w === 'number' ? euro(w) : w}</td></tr>)}
      </tbody></table>
    )}
    {fruehAktiv && <p className="au-k-info">Die Preise oben sind die heutigen, der Frühbucher-Nachlass ist schon eingerechnet.</p>}
    {(t.zahlungsweise || t.zahlungsfrist || t.abmelde_regel) && <>
      <h3>Bezahlen und Abmelden</h3>
      <KV paare={[['Zahlungsweise', t.zahlungsweise], ['Zahlungsfrist', t.zahlungsfrist], ['Abmeldung', t.abmelde_regel]]} />
    </>}
  </>, !preisZeilen.length && !t.zahlungsweise && !t.zahlungsfrist && !t.abmelde_regel);

  const ortSuche = encodeURIComponent([t.halle_name, t.adresse, t.plz, t.stadt].filter(Boolean).join(' ') || t.ort || '');
  dazu('anfahrt', 'Ort und Anfahrt', 'ort', <>
    <p style={{ color: 'var(--au-text)', fontWeight: 600, margin: 0 }}>{halle}</p>
    {anschriftZeigen && <p>{anschriftZeigen}</p>}
    <KV paare={[['Parken', t.parken], ['Bus und Bahn', t.oepnv], ['Barrierefrei', t.barrierefrei]]} />
    {ortSuche && (
      <p style={{ marginTop: 10 }}>
        <a href={`https://www.openstreetmap.org/search?query=${ortSuche}`} target="_blank" rel="noopener noreferrer">In Karte öffnen</a>
        {' · '}
        <a href={`https://www.google.com/maps/dir/?api=1&destination=${ortSuche}`} target="_blank" rel="noopener noreferrer">Route planen</a>
      </p>
    )}
  </>, !halle && !anschrift);

  const kzNachDisziplin = (t.kampfzeiten || []).map((k) => [k.disziplin ? `${k.disziplin} · ${k.klasse}` : k.klasse, k.zeit]);
  dazu('regeln', 'Regeln und Ausrüstung', 'regel', <>
    <KV paare={[['Regelwerk', t.regeln]]} />
    {kzNachDisziplin.length > 0 && <><h3>Kampfzeiten</h3><KV paare={kzNachDisziplin} /></>}
    {(t.schutz || []).length > 0 && <><h3>Schutzausrüstung</h3><KV paare={t.schutz.map((s) => [s.disziplin, s.ausruestung])} /></>}
  </>, !t.regeln && !kzNachDisziplin.length && !(t.schutz || []).length);

  const kampfrichter = [t.kampfrichter_regel, betrag(t.kampfrichter_ausfallgebuehr) > 0 ? `Ausfallgebühr je fehlendem Kampfrichter: ${euro(t.kampfrichter_ausfallgebuehr)}` : ''].filter(Boolean).join('\n');
  dazu('teilnahme', 'Teilnahme, Haftung, Datenschutz', 'schild', <>
    <KV paare={[['Startberechtigt', t.startberechtigung], ['Pass oder Lizenz', t.pass_pflicht], ['Altersnachweis', t.altersnachweis],
      ['Versicherung', t.versicherung_text], ['Haftung', t.haftung_text]]} />
    {(kampfrichter || t.coach_regel) && <><h3>Kampfrichter und Coaches</h3><KV paare={[['Kampfrichter', kampfrichter], ['Coaches', t.coach_regel]]} /></>}
    {t.datenschutz_text && <><h3>Datenschutz und Fotos</h3><p className="au-text-block">{t.datenschutz_text}</p></>}
  </>, !(t.startberechtigung || t.pass_pflicht || t.altersnachweis || t.versicherung_text || t.haftung_text || kampfrichter || t.coach_regel || t.datenschutz_text));

  dazu('vorort', 'Vor Ort und Übernachtung', 'haus', <>
    <KV paare={[['Zuschauer', t.zuschauer_eintritt], ['Verpflegung', t.verpflegung]]} />
    {(t.hotels || []).length > 0 && <>
      <h3>Übernachtung</h3>
      <ul className="au-lst">
        {t.hotels.map((h) => (
          <li key={h.id}><Icon n="bett" /><div>
            <b>{h.link ? <a href={h.link} target="_blank" rel="noopener noreferrer">{h.name}</a> : h.name}</b>
            {h.entfernung && <> · {h.entfernung} zur Halle</>}
            {h.hinweis && <><br /><span style={{ color: 'var(--au-text-3)', fontSize: 13 }}>{h.hinweis}</span></>}
          </div></li>
        ))}
      </ul>
    </>}
  </>, !t.zuschauer_eintritt && !t.verpflegung && !(t.hotels || []).length);

  dazu('kontakt', 'Kontakt', 'post', (
    <ul className="au-lst">
      {t.kontakt_email && <li><Icon n="post" /><div><a href={`mailto:${t.kontakt_email}`}>{t.kontakt_email}</a></div></li>}
      {t.kontakt_telefon && <li><Icon n="tel" /><div><a href={`tel:${String(t.kontakt_telefon).replace(/\s/g, '')}`}>{t.kontakt_telefon}</a></div></li>}
      {t.website && /^https?:\/\//i.test(t.website) && <li><Icon n="web" /><div><a href={t.website} target="_blank" rel="noopener noreferrer">{t.website.replace(/^https?:\/\//i, '')}</a></div></li>}
    </ul>
  ), !t.kontakt_email && !t.kontakt_telefon && !t.website);

  dazu('downloads', 'Downloads', 'datei', (
    <div className="au-dl">
      <a href={t.pdf_url} target="_blank" rel="noopener noreferrer"><span className="au-typ">PDF</span><span>Ausschreibung<small>Kurzfassung zum Ausdrucken</small></span></a>
      {(t.dokumente || []).map((d) => (
        <a key={d.id} href={d.url} target="_blank" rel="noopener noreferrer"><span className="au-typ">PDF</span><span>{d.titel}<small>PDF, {groesse(d.groesse)}</small></span></a>
      ))}
    </div>
  ), !t.pdf_url);

  // ── Kopf ──
  const fakten = [
    ['kal', 'Datum', von ? (bis ? `${datKurz(von)} bis ${datKurz(bis)}` : datLang(von)) : (t.datum_text || 'Termin folgt'), t.einlass_zeit ? `Einlass ${uhr(t.einlass_zeit)}` : ''],
    ['ort', 'Ort', halle, anschriftZeigen],
    ['waage', 'Waage', waage, ''],
    ['gruppe', 'Gemeldet', t.gemeldet ? `${t.gemeldet} Starter` : '', t.max_teilnehmer ? `höchstens ${t.max_teilnehmer}` : ''],
  ].filter((x) => x[2]);
  const plakat = t.plakat_url;
  const logo = !plakat ? logoFuer(t) : null;

  return rahmen(
    <div className="au-wrap">
      <div className="au-d-oben">
        <button type="button" className="au-zurueck" onClick={() => navigate('/')}><Icon n="links" />Alle Veranstaltungen</button>
        <ThemaKnopf wirksam={wirksam} umschalten={umschalten} />
      </div>
      <div className="au-d-kopf" ref={kopfRef}>
        <button type="button" className={`au-plakat${logo ? ' au-mit-logo' : ''}`} onClick={() => setGross(true)} aria-label={plakat ? 'Plakat groß ansehen' : 'Logo groß ansehen'}>
          <img src={plakat || logo} alt={plakat ? `Plakat ${t.name}` : ''} onError={(e) => { e.currentTarget.src = '/logo.webp'; }} />
        </button>
        {gross && (
          <div className="au-gross" role="dialog" aria-modal="true" aria-label={t.name} onClick={() => setGross(false)} onKeyDown={(e) => { if (e.key === 'Escape') setGross(false); }} tabIndex={-1} ref={(el) => el && el.focus()}>
            <img src={plakat || logo} alt={plakat ? `Plakat ${t.name}` : t.name} onError={(e) => { e.currentTarget.src = '/logo.webp'; }} />
            <button type="button" className="au-gross-zu" aria-label="Schließen">×</button>
          </div>
        )}
        <div className="au-d-titel">
          <div className="au-reihe"><span className="au-art">{t.art || 'Turnier'}</span><StatusAbzeichen anmeldung={a} /></div>
          <h1>{t.name}</h1>
          {(ausrichter || veranstalter) && (
            <div className="au-veranst">{ausrichter || veranstalter}{ausrichter && veranstalter && ausrichter !== veranstalter ? ` · im Auftrag von ${veranstalter}` : ''}</div>
          )}
          <dl className="au-fakten">
            {fakten.map(([i, k, w, z]) => <div key={k}><Icon n={i} /><span><dt>{k}</dt><dd>{w}{z && <small>{z}</small>}</dd></span></div>)}
          </dl>
        </div>
        <div className="au-anm">
          {ziel ? <>
            <div className="au-anm-l">{a.bis_turniertag ? 'Anmeldung offen bis Turnierbeginn' : 'Meldeschluss in'}</div>
            <Zaehler ziel={ziel} />
            <div className="au-anm-datum">{a.bis_turniertag ? `Ohne Meldeschluss: bis ${datLang(tag(a.bis))}` : `${datLang(tag(a.bis))}, 23:59 Uhr`}</div>
          </> : <>
            <div className="au-anm-l">Anmeldung</div>
            <div><StatusAbzeichen anmeldung={a} /></div>
            {a.zusatz && <div className="au-anm-datum">{a.zusatz}</div>}
          </>}
          {preisAb != null && !['erg', 'vorbei', 'abgesagt'].includes(a.zustand) && <div className="au-anm-preis"><span>Startgeld ab</span><b>{preisAb > 0 ? euro(preisAb) : 'frei'}</b></div>}
          <button type="button" className="au-knopf au-haupt" disabled={knopfAus} onClick={knopfKlick}>{knopf.text}<Icon n="pfeil" /></button>
          {t.pdf_url && <a className="au-knopf au-neben" href={t.pdf_url} target="_blank" rel="noopener noreferrer"><Icon n="datei" />Ausschreibung als PDF</a>}
          {t.max_teilnehmer
            ? <><div className="au-balken" title={`${anteil} % belegt`}><i style={{ width: `${anteil}%` }} /></div><div className="au-anm-klein">{t.gemeldet} von {t.max_teilnehmer} Plätzen belegt{beschraenkt ? ' · nur zugelassene Vereine' : ''}</div></>
            : <div className="au-anm-klein">{beschraenkt ? 'Nur für zugelassene Vereine' : 'Für Vereine und Einzelstarter'}</div>}
        </div>
      </div>

      <nav className={`au-sprung${klein ? ' au-klein' : ''}`} aria-label="Abschnitte">
        <div className="au-sprung-in">
          {teile.map((x) => <a key={x.id} href={`#${x.id}`} className={aktiv === `#${x.id}` ? 'au-aktiv' : ''}>{KURZ[x.id]}</a>)}
          {a.anmeldbar && <button type="button" className="au-mini" onClick={anmelden}>Jetzt anmelden</button>}
        </div>
      </nav>

      <div className="au-d-raster">
        <div>{teile.map((x) => <Abschnitt key={x.id} id={x.id} titel={x.titel} icon={x.icon}>{x.inhalt}</Abschnitt>)}</div>
        <aside className="au-seite">
          <div className="au-blick"><h3>Auf einen Blick</h3>
            <dl className="au-kv">
              <dt>Datum</dt><dd>{von ? `${datKurz(von)}${bis ? ` bis ${datKurz(bis)}` : ''}` : (t.datum_text || 'folgt')}</dd>
              {(t.stadt || t.ort) && <><dt>Ort</dt><dd>{t.stadt || t.ort}</dd></>}
              <dt>Meldeschluss</dt><dd>{t.anmeldeschluss_iso ? datKurz(tag(t.anmeldeschluss_iso)) : (t.nachmeldung_bis_text ? `keiner, Anmeldung bis ${t.nachmeldung_bis_text}` : 'keiner, Anmeldung auch am Turniertag bis zum Start der Kategorie')}</dd>
              <dt>Nachmeldung</dt><dd>{t.nachmeldung_bis_text ? `bis ${t.nachmeldung_bis_text}, ` : ''}am Turniertag nur für Kategorien, die noch nicht gestartet sind</dd>
              {preisAb != null && <><dt>Startgeld</dt><dd>ab {preisAb > 0 ? euro(preisAb) : 'frei'}</dd></>}
              {t.kontakt_email && <><dt>Kontakt</dt><dd><a href={`mailto:${t.kontakt_email}`}>{t.kontakt_email}</a></dd></>}
            </dl>
          </div>
          <div className="au-live"><Icon n="live" />
            {t.live_url
              ? <span><b><Link to={t.live_url}>Live-Ergebnisse</Link></b><br />Aufrufe, Kämpfe und Platzierungen.</span>
              : <span><b>Live-Ergebnisse</b><br />Ab dem Turniertag hier: Aufrufe, Kämpfe und Platzierungen.</span>}
          </div>
        </aside>
      </div>
      {(a.anmeldbar || a.zustand === 'voll') && (
        <div className="au-mobil-anm" ref={leisteRef}>
          <div><b>{a.anmeldbar ? (a.bis_kategoriestart ? (t.nachmeldung_bis_text ? `Anmeldung bis ${t.nachmeldung_bis_text}` : 'Anmeldung bis zum Start der Kategorie') : a.bis_turniertag ? 'Anmeldung bis zum Turniertag' : `Meldeschluss ${datKurz(tag(a.bis))}`) : 'Ausgebucht'}</b>{preisAb != null ? `Startgeld ab ${preisAb > 0 ? euro(preisAb) : 'frei'}` : ''}</div>
          <button type="button" className="au-knopf au-haupt" disabled={knopfAus} onClick={knopfKlick}>{knopf.text}</button>
        </div>
      )}
    </div>,
  );
}
